import { RefreshCw, WifiOff } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { type ErrorContext, isReportSignInFormOpen, ReportSignInIssue } from './ReportSignInIssue'

type Props = {
  readonly subtitle: string
  readonly onRetry: () => unknown
  readonly errorContext?: ErrorContext
  readonly prefillEmail?: string
}

export function ConnectionProblemPage({ subtitle, onRetry, errorContext, prefillEmail }: Props) {
  const { t } = useTranslation()
  const [isRetrying, setIsRetrying] = useState(false)
  const [isReportingIssue, setIsReportingIssue] = useState(isReportSignInFormOpen)

  async function handleRetry() {
    setIsRetrying(true)
    try {
      await Promise.resolve(onRetry())
    } finally {
      setIsRetrying(false)
    }
  }

  return (
    <div className='flex min-h-screen items-center justify-center bg-linear-to-br from-blue-50 to-slate-100 p-4'>
      <div className='w-full max-w-lg'>
        <div className='mb-6 flex justify-center'>
          <img src='/engen-logo.png' alt='EnGen' className='h-8' />
        </div>
        <div className='overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl'>
          {/* Header with icon */}
          <div className='flex flex-col items-center px-8 pt-10 pb-6'>
            <div className='mb-6 rounded-full bg-blue-100 p-4'>
              <WifiOff className='h-10 w-10 text-blue-600' strokeWidth={2} />
            </div>
            <h1 className='text-center text-2xl font-semibold tracking-tight text-slate-900'>
              {t('connectionProblem.title')}
            </h1>
            <p className='mt-1 text-sm text-slate-400'>{subtitle}</p>
          </div>

          {/* Body */}
          <div className='px-8 pb-6'>
            <p className='text-center leading-relaxed text-slate-600'>{t('connectionProblem.body')}</p>
          </div>

          {/* Troubleshooting steps */}
          <div className='mx-8 mb-6 rounded-xl border border-slate-100 bg-slate-50 p-4'>
            <p className='mb-3 text-sm font-medium text-slate-700'>{t('connectionProblem.thingsToTry')}</p>
            <ol className='space-y-2 text-sm text-slate-500'>
              <li className='flex gap-2'>
                <span>1.</span>
                <span>{t('connectionProblem.step1')}</span>
              </li>
              <li className='flex gap-2'>
                <span>2.</span>
                <span>{t('connectionProblem.step2', { domain: 'engen.cloud' })}</span>
              </li>
              <li className='flex gap-2'>
                <span>3.</span>
                <span>{t('connectionProblem.step3')}</span>
              </li>
            </ol>
          </div>

          {/* Actions */}
          <div className='space-y-3 border-t border-slate-100 bg-slate-50/50 px-8 py-6'>
            {!isReportingIssue && (
              <button
                type='button'
                onClick={handleRetry}
                disabled={isRetrying}
                className='flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-3 text-sm font-medium text-white transition-colors duration-200 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2 disabled:opacity-60'
              >
                <RefreshCw className={`h-4 w-4 ${isRetrying ? 'animate-spin' : ''}`} />
                {isRetrying ? t('connectionProblem.trying') : t('connectionProblem.tryAgain')}
              </button>
            )}
            {errorContext && (
              <ReportSignInIssue
                variant='link'
                onOpenChange={setIsReportingIssue}
                errorContext={errorContext}
                prefillEmail={prefillEmail}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
